import { useState } from 'react'

import { Box, Button, Divider, Radio } from '@factorialco/deprecated-design-system'
import {
  InvoiceFragment,
  SoftwareManagementExchangeCurrencyFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/Inputs/DayPicker'
import MoneyField from 'modules/core/components/Inputs/MoneyField'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { FileWithStatus } from 'modules/finance/components/FileWithStatusDropzone'
import { InvoicesDropzone } from 'modules/finance/components/InvoicesDropzone'
import CurrencySelector from 'modules/settings/components/CurrencySelector'
import { useCreateSoftwarePayment } from 'modules/software_management/hooks/useCreateSoftwarePayment'
import { useApplicationsContext } from 'modules/software_management/pages/ApplicationsPage'

import SoftwarePaymentForm from 'modules/software_management/components/ApplicationDetailsContent/PaymentsWidget/SoftwarePaymentForm'

interface Props {
  softwareSetupId: string
  onSuccess: () => void
  onClose: () => void
  onUploadInvoice: (invoice: InvoiceFragment) => void
  currencies: SoftwareManagementExchangeCurrencyFragment[]
}

const CreatePaymentModal = ({
  softwareSetupId,
  onSuccess,
  onClose,
  onUploadInvoice,
  currencies,
}: Props) => {
  const { currency: userCurrency } = useApplicationsContext()
  const { company } = useCurrentSession()
  const [mode, setMode] = useState<'simple_form' | 'import_file' | ''>('')
  const [fileStatus, setFilesStatus] = useState<FileWithStatus<InvoiceFragment>>()

  const { create: createSoftwarePayment, loading: creating } = useCreateSoftwarePayment()

  const form = useLazyRef(() => new SoftwarePaymentForm({})).current

  const allowedCurrencies = currencies.map(({ id }) => id)

  const onSubmit = async () => {
    const { amount, currency, date } = form.data()

    const newDate = dateToString(stringToDate(date, 'backend'), 'backend_datetime')

    const result = await createSoftwarePayment(
      {
        softwareSetupId,
        amount,
        currency,
        date: newDate,
      },
      userCurrency,
      company.id
    )

    const handled = handleResult(result, (formErrors) => {
      app.setFlash({
        type: 'wrong',
        message: formErrors.messages || i18n.t('errors.generic_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('software_management.software_setups.create_payment_modal.success'),
      })
      onSuccess()
    }
  }

  return (
    <Modal
      illustration="money-bag"
      title={i18n.t('software_management.software_setups.create_payment_modal.title')}
      onClose={onClose}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={onClose}>
            {i18n.t('software_management.software_setups.create_payment_modal.cancel')}
          </Button>
          {mode === 'simple_form' && (
            <Button disabled={creating} onClick={onSubmit}>
              {i18n.t('common.submit')}
            </Button>
          )}
          {mode === 'import_file' && (
            <Button
              disabled={!fileStatus}
              onClick={() => {
                const invoice = fileStatus?.result
                if (invoice) {
                  onUploadInvoice(invoice)
                }
              }}
            >
              {i18n.t('software_management.software_setups.create_payment_modal.review')}
            </Button>
          )}
        </Box>
      }
    >
      <Box gap="s16" flexDirection="column">
        <Box gap="s8" flexDirection="column">
          <Radio
            name="visibility"
            description={i18n.t(
              'software_management.software_setups.create_payment_modal.description_manual'
            )}
            option={{
              label: i18n.t(
                'software_management.software_setups.create_payment_modal.title_manual'
              ),
              value: 'simple_form',
              disabled: false,
            }}
            value={mode ?? ''}
            // eslint-disable-next-line no-type-assertion/no-type-assertion
            onChange={(value) => setMode(value as 'simple_form' | 'import_file' | '')}
          />
          <Radio
            name="visibility"
            description={i18n.t(
              'software_management.software_setups.create_payment_modal.description_import_file'
            )}
            option={{
              label: i18n.t(
                'software_management.software_setups.create_payment_modal.title_import_file'
              ),
              value: 'import_file',
              disabled: false,
            }}
            value={mode}
            // eslint-disable-next-line no-type-assertion/no-type-assertion
            onChange={(value) => setMode(value as 'simple_form' | 'import_file' | '')}
          />
        </Box>
        {mode !== '' && <Divider />}
        {mode === 'simple_form' && (
          <>
            <MoneyField
              mandatory
              label={i18n.t('software_management.software_setups.create_payment_modal.amount')}
              field={form.get('amount')}
              currency={form.get('currency').value}
              placeholder="0"
            />
            <CurrencySelector
              field={form.get('currency')}
              label={i18n.t('software_management.software_setups.create_payment_modal.currency')}
              filteredCurrencies={allowedCurrencies}
            />
            <DayPicker
              mandatory
              field={form.get('date')}
              label={i18n.t('software_management.software_setups.create_payment_modal.date')}
              maxDate={moment().endOf('day')}
            />
          </>
        )}
        {mode === 'import_file' && (
          <InvoicesDropzone
            multiple={false}
            values={fileStatus ? [fileStatus] : []}
            onUploadFinish={(fileStatusList) => {
              setFilesStatus(fileStatusList?.[0])
            }}
            onFileRemove={() => setFilesStatus(undefined)}
            resources={[{ id: softwareSetupId, type: 'softwaresetup' }]}
          />
        )}
      </Box>
    </Modal>
  )
}

export default CreatePaymentModal
